import { useDesignParams } from '@apps/createWedding/hooks/useDesignParams';
import { useCreateWeddingTelemetry } from '@apps/createWedding/CreateWedding.telemetry';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useFeatureValue } from '@shared/core/featureFlags';
import { SurveyItems } from '../routes/Survey/Survey.types';
import { useMemo } from 'react';

enum Scenario {
  Invitation = 'invitation',
  SaveTheDate = 'save-the-date',
  Registry = 'registry'
}

export const scenarioRedirectRoutes: Record<Scenario, string> = {
  invitation: '/ecard/invitation',
  'save-the-date': '/ecard/savethedate',
  registry: '/registry/build'
};

type RedirectFromTyingTheKnotFn = (website: string) => void;

export const useGetRedirectFromTyingTheKnot: () => RedirectFromTyingTheKnotFn = () => {
  const { designParams, cleanDesignParams } = useDesignParams();
  const telemetry = useCreateWeddingTelemetry();
  const queryParamHelper = useQueryParamHelper();

  const scenario = `${designParams.scenario}`;

  const onboardingInterests = queryParamHelper.getValueString('onboardingInterests') ?? '';
  const onboardingInterestsArray = useMemo(() => {
    return queryParamHelper.getValueArray('onboardingInterests') || [];
  }, [queryParamHelper]);

  // 1. If user selects only 1 option and it is std/invitations, redirect to admin card home
  // 2. if user seletcs 2 options and it is std/invitations and guest list, redirect to admin card home
  let targetProduct = useMemo((): 'card' | 'registry' | undefined => {
    switch (onboardingInterestsArray.length) {
      case 1: {
        if (onboardingInterestsArray[0] === SurveyItems.INVITATION_SAVE_THE_DATES) {
          return 'card';
        } else if (onboardingInterestsArray[0] === SurveyItems.REGISTRY) {
          return 'registry';
        }
        break;
      }
    }

    return;
  }, [onboardingInterestsArray]);

  const isPrintCreateWeddingRedirectEnabled = useFeatureValue('printCreateWeddingRedirect', { skip: targetProduct !== 'card' }).value === 'treatment';

  if (!isPrintCreateWeddingRedirectEnabled && targetProduct === 'card') {
    // If user is not in the print experiment, reset the target product redirect
    targetProduct = undefined;
  }

  return (website: string) => {
    cleanDesignParams();
    let redirectUrl: string;
    if (scenario === 'marcom-shop' && designParams.destination) {
      const nextUrl = new URL(decodeURIComponent(designParams.destination), window.location.origin);

      if (nextUrl.origin !== window.location.origin) {
        telemetry.maliciousRedirect(nextUrl.href);

        redirectUrl = new URL(`/shop${onboardingInterests ? `?onboardingInterests=${onboardingInterests}` : ''}`, window.location.origin).href;
      } else {
        redirectUrl = nextUrl.href;
      }

      window.location.href = nextUrl.href;
    } else if ((scenario === 'save-the-date' || scenario === 'invitation') && designParams.destination) {
      const basePath = `/${website}`;
      const nextUrl = new URL(basePath + decodeURIComponent(designParams.destination), window.location.origin);

      if (nextUrl.origin !== window.location.origin) {
        telemetry.maliciousRedirect(nextUrl.href);

        redirectUrl = new URL(basePath + '/edit/cards', window.location.origin).href;
      } else {
        redirectUrl = nextUrl.href;
      }
    } else {
      const basePathMapByProduct = {
        registry: `/${website}/edit/registry`,
        card: `/${website}/edit/cards`
      };
      const basePath = (targetProduct && basePathMapByProduct[targetProduct]) || `/${website}/edit`;

      redirectUrl = new URL(basePath + (onboardingInterests ? `?onboardingInterests=${onboardingInterests}` : ''), window.location.origin).href;
    }

    window.location.href = redirectUrl;
  };
};
